import React, { useState, useEffect, useCallback } from 'react';
import { peek, put } from '../data/routeData';
import { squareKey, loadSquare } from '../data/routeLoaders';
import RouteLoadError from '../components/transitions/RouteLoadError';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import IntroductionFlow from '../components/introduction/IntroductionFlow';
import { safeReturnTo } from '../utils/returnTo';
import Navbar from '../components/Navbar';
import Modal, { modalChoice, modalRow } from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import SignalCard from '../components/SignalCard';
import SquareHeader from '../components/SquareHeader';
import CityPicker from '../components/CityPicker';
import TownSquareScreen from '../components/townsquare/TownSquareScreen';
import StoryViewer from '../components/stories/StoryViewer';
import useReelParam from '../hooks/useReelParam';
import { subscribePresence, subscribeSquareEvents } from '../services/squarePresence';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { squareService, notificationService } from '../services';
import { getCity } from '../services/userLocation';
import { SPECIES } from '../theme/species';
import { isPlaceholderPlace, realPlace } from '../utils/placeLabel';
import { reverseGeocode, repairStoredLocation } from '../utils/reverseGeocode';

const LOCATION_KEY = 'pr.userLocation';
function readLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * SquarePage — the City Portal destination (Phase 3). `/square` resolves the
 * caller's local town from stored coords and redirects to `/square/:citySlug`,
 * which shows the city banner + live signal feed. Change town via the header ▾.
 */
export default function SquarePage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Sign-up's first landing with a photo: `?welcome=1` offers "Make an introduction." on top of
  // the real square. Once settled the parameter goes, so a reload is just the square.
  const [welcoming, setWelcoming] = useState(() => searchParams.get('welcome') === '1');
  const { profile, walletBalance, role } = useUser();
  const { user, logout } = useAuth();
  const isAdmin = ['admin', 'manager', 'superadmin'].includes(role);

  const cached = peek(squareKey(citySlug));
  const [square, setSquare] = useState(cached?.square || null);
  const [feed, setFeed] = useState(cached?.feed || []);
  const [town, setTown] = useState(cached?.town || null); // City Portal v2 snapshot (flag-gated)
  const [loading, setLoading] = useState(!cached);
  const [loadError, setLoadError] = useState(null);
  const [extrasLoading, setExtrasLoading] = useState(false); // slow external boxes
  const [presence, setPresence] = useState(null); // realtime { count, initials } who's here now
  // Remember whether v2 was on last time so we can render the v2 skeleton
  // immediately on load instead of flashing the v1 screen while /town resolves.
  const [v2Hint] = useState(() => { try { return localStorage.getItem('pr.townV2') === '1'; } catch { return false; } });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [nearby, setNearby] = useState([]);
  const [regenerating, setRegenerating] = useState(false);
  const [reportFor, setReportFor] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const cyan = SPECIES.signal;

  // The story viewer rides its own history entry (?reel=), as on the wall, so back closes it;
  // a reload or a shared link opens it too (useReelParam).
  const reelLocation = square && typeof square.centerLat === 'number' ? { lat: square.centerLat, lng: square.centerLng } : null;
  const { openReelUserId, reels: reelList, doorRect, openReel, closeReel, openNonce } = useReelParam({ location: reelLocation });

  const submitReport = async (category) => {
    if (!reportFor) return;
    setReportBusy(true);
    await squareService.report(citySlug, { eventId: reportFor.eventId, category });
    setReportBusy(false);
    setReportFor(null);
    load();
  };

  // Mute a signal's host (Phase 3c) — silences their signals for you, and drops them from the feed here.
  const muteHost = async (s) => {
    if (!s.hostUserId) return;
    const r = await notificationService.addMute('person', s.hostUserId);
    if (r.success) setFeed((prev) => prev.filter((x) => x.hostUserId !== s.hostUserId));
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    const r = await squareService.regenerateBanner(citySlug);
    if (r.success) setSquare(r.data); // Mapbox placeholder; the poll swaps in the new AI image
    setRegenerating(false);
  };

  // Switch cities the instant one is picked. The header swaps to the new city's name
  // immediately (getSquare fills the banner/map a moment later) instead of the old city
  // sitting there, and the ticker + body drop to skeletons right away. Fixes the "keeps
  // the current city for ~10s" confusion — nothing waits on the slow /town snapshot.
  const beginCitySwitch = (target) => {
    setPickerOpen(false);
    setLoading(true);
    setExtrasLoading(true);
    setTown(null);
    setFeed([]);
    setPresence(null);
    if (target?.label) setSquare({ slug: target.slug || null, label: target.label, liveCount: 0 });
  };

  // Global city search (change-town) → resolve that city, then go to its square.
  const goToCity = async (city) => {
    if (!city) { setPickerOpen(false); return; }
    beginCitySwitch({ label: city.label, slug: city.slug });
    const r = await squareService.resolve({ lat: city.lat, lng: city.lng, label: city.label });
    if (r.success && r.data?.slug) navigate(`/square/${r.data.slug}`);
    else setLoading(false);
  };

  // Jump to a nearby town / pill. Smaller towns may not have a Squares row yet, so
  // resolve first (get-or-create with the town's coords) — otherwise getSquare 404s
  // and the page would keep showing the previous town.
  const goToTown = async (t) => {
    if (!t || t.slug === citySlug) { setPickerOpen(false); return; }
    beginCitySwitch(t);
    if (t.lat != null && t.lng != null) {
      const r = await squareService.resolve({ lat: t.lat, lng: t.lng, label: t.label });
      if (r.success && r.data?.slug) { navigate(`/square/${r.data.slug}`); return; }
    }
    navigate(`/square/${t.slug}`);
  };

  // No slug → resolve the local square from stored location (city label), redirect.
  useEffect(() => {
    if (citySlug) return undefined;
    let cancelled = false;
    (async () => {
      const loc = (await repairStoredLocation()) || readLocation();
      const r = await squareService.resolve({ lat: loc?.lat, lng: loc?.lng, label: realPlace(loc?.label) || getCity() || undefined });
      if (cancelled) return;
      if (r.success && r.data?.slug) navigate(`/square/${r.data.slug}`, { replace: true });
      // Home is where signing in lands, so it can never be left blank.
      else navigate('/featured', { replace: true });
    })();
    return () => { cancelled = true; };
  }, [citySlug, navigate]);

  // Cities-with-content pills (also feeds the change-town modal).
  const loadNearby = useCallback(async () => {
    const loc = readLocation();
    // Anchor "towns within 50 miles" to the town you're actually viewing (its own centre),
    // falling back to the browser's stored location — otherwise the Overpass radius search
    // never runs and the picker only shows the current city.
    const lat = (typeof square?.centerLat === 'number' && square.centerLat !== 0) ? square.centerLat : loc?.lat;
    const lng = (typeof square?.centerLng === 'number' && square.centerLng !== 0) ? square.centerLng : loc?.lng;
    const label = realPlace(square?.label, loc?.label) || undefined;
    const r = await squareService.getNearby({ lat, lng, label });
    if (r.success) setNearby(r.data || []);
  }, [square?.centerLat, square?.centerLng, square?.label]);

  const load = useCallback(async () => {
    if (!citySlug) return;
    setLoading(!peek(squareKey(citySlug)));
    setExtrasLoading(true); // ticker + boxes skeleton from the very start
    setLoadError(null);
    // On a cold entry the name shows instantly from the slug; a picker switch already
    // set the nicer label, which this preserves (getSquare replaces both shortly).
    setSquare((prev) => prev || { slug: citySlug, label: realPlace(citySlug.replace(/-/g, ' ')), liveCount: 0 });

    // Header + feed first — fast. The banner (cover image, or the map when there's no
    // cover) and its info paint the moment getSquare returns, without waiting on the
    // slower /town snapshot. This is what fixes the "old city / no photo for ~10s" lag.
    let initial;
    try { initial = await loadSquare(citySlug); }
    catch (error) { setLoadError(error.message); setLoading(false); setExtrasLoading(false); return; }
    Promise.resolve({ success: true, data: initial.square }).then(async (sq) => {
      // The server can answer with a different town than the URL asked for — a leftover
      // placeholder square ("your-location") resolves to the real town at its coordinates.
      // Follow it, so the page is that town's page, name and all.
      if (sq.success && sq.data?.slug && sq.data.slug !== citySlug) {
        navigate(`/square/${sq.data.slug}`, { replace: true });
        return;
      }
      // An older server still hands the placeholder square back as-is. Name the real town at
      // its coordinates from here, without the label, and go to that town's page instead.
      if (sq.success && isPlaceholderPlace(sq.data?.label) && typeof sq.data?.centerLat === 'number') {
        const town = await reverseGeocode(sq.data.centerLat, sq.data.centerLng);
        const real = await squareService.resolve({ lat: sq.data.centerLat, lng: sq.data.centerLng, label: town || undefined });
        if (real.success && real.data?.slug && real.data.slug !== citySlug && !isPlaceholderPlace(real.data.label)) {
          navigate(`/square/${real.data.slug}`, { replace: true });
          return;
        }
      }
      setSquare(sq.success ? sq.data : { slug: citySlug, label: realPlace(citySlug.replace(/-/g, ' ')), liveCount: 0 });
    });
    setFeed(initial.feed);

    // Live-data snapshot — can be slower; its arrival drops the body skeleton.
    const townData = initial.town;
    setTown(townData);
    try { localStorage.setItem('pr.townV2', townData?.v2Enabled ? '1' : '0'); } catch { /* ignore */ }
    setLoading(false);

    // Each slow/external box is its own call, so the fast ones (sports, headlines)
    // appear immediately and the slow ones (open-now) trail — each with its own
    // skeleton until its field is set.
    if (townData?.v2Enabled) {
      const boxes = [
        ['openNow', 'opennow'], ['headlines', 'headlines'], ['sports', 'sports'],
        ['newcomers', 'newcomers'], ['locals', 'locals'], ['deals', 'deals'],
        ['alert', 'alert'], ['bulletin', 'bulletin'],
      ];
      const tickerFor = (name, mod) => {
        const d = mod?.data;
        if (name === 'sports' && d?.team && d?.line) return `${d.team} ${d.line}`.toUpperCase();
        if (name === 'alert' && d?.text) return d.text.toUpperCase();
        return null;
      };
      const loc = readLocation();
      const calls = boxes.map(([field, name]) =>
        squareService.getTownModule(citySlug, name, name === 'opennow' && loc?.lat ? { lat: loc.lat, lng: loc.lng } : undefined).then((r) => {
          const mod = r.success ? r.data : { source: '', data: null };
          const extra = tickerFor(name, mod);
          setTown((prev) => (prev ? {
            ...prev,
            [field]: mod,
            ticker: extra && !(prev.ticker || []).includes(extra) ? [...(prev.ticker || []), extra] : prev.ticker,
          } : prev));
        }),
      );
      calls.push(squareService.getTownModule(citySlug, 'water').then((r) => {
        const d = r.success ? r.data : null;
        if (d && (d.lakeName || d.lakeTempC != null)) {
          setTown((prev) => (prev && prev.conditions?.data ? {
            ...prev,
            conditions: { ...prev.conditions, data: {
              ...prev.conditions.data,
              lakeName: d.lakeName || prev.conditions.data.lakeName,
              lakeTempC: d.lakeTempC != null ? d.lakeTempC : prev.conditions.data.lakeTempC,
            } },
          } : prev));
        }
      }));
      await Promise.allSettled(calls);
      setExtrasLoading(false);
    } else {
      setExtrasLoading(false);
    }
  }, [citySlug, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (square?.slug === citySlug && town) put(squareKey(citySlug), { square, feed, town });
  }, [citySlug, square, feed, town]);
  useEffect(() => { loadNearby(); }, [loadNearby, citySlug]);

  // Real-time presence — join this square's socket group and update "who's here now"
  // live as people pop in / leave. Only for v2 (the flag we already know).
  useEffect(() => {
    if (!citySlug || !(town?.v2Enabled || v2Hint)) return undefined;
    let cleanup; let cancelled = false;
    setPresence(null);
    subscribePresence(citySlug, (p) => { if (!cancelled) setPresence(p); })
      .then((fn) => { cleanup = fn; if (cancelled && fn) fn(); });
    return () => { cancelled = true; if (cleanup) cleanup(); };
  }, [citySlug, town?.v2Enabled, v2Hint]);

  // Live event refresh — when Eventbrite/Ticketmaster (etc.) just ingested events for this
  // city, the server pushes "eventsUpdated"; re-pull the snapshot + feed so someone sitting
  // on the page sees them appear without a manual refresh. Debounced against a burst.
  useEffect(() => {
    if (!citySlug) return undefined;
    let timer = null;
    const off = subscribeSquareEvents((info) => {
      if (info?.slug && info.slug !== citySlug) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        squareService.getTown(citySlug).then((tn) => { if (tn.success) setTown(tn.data); });
        squareService.getFeed(citySlug).then((fd) => setFeed(fd.success ? (fd.data || []) : []));
      }, 1500);
    });
    return () => { if (timer) clearTimeout(timer); off(); };
  }, [citySlug]);

  // The OpenAI banner generates in the background (~1 min). Poll a few times and
  // swap it in when ready, replacing the instant Mapbox satellite placeholder.
  useEffect(() => {
    if (!citySlug || square?.bannerSource === 'openai') return undefined;
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      const r = await squareService.getSquare(citySlug);
      if (r.success && r.data?.bannerSource === 'openai') {
        setSquare(r.data);
        clearInterval(id);
      } else if (tries >= 8) {
        clearInterval(id);
      }
    }, 20000);
    return () => clearInterval(id);
  }, [citySlug, square?.bannerSource]);

  const openPicker = () => setPickerOpen(true);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const welcomeDone = () => {
    setWelcoming(false);
    const next = safeReturnTo(searchParams.get('next'));
    navigate(next || `/square/${citySlug}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-inside-bg">
      {welcoming && citySlug && (
        <IntroductionFlow
          // The town the sign-up counted for (`?city=`), else this square's label.
          city={searchParams.get('city') || square?.label || realPlace(citySlug.replace(/-/g, ' ')) || citySlug}
          onDone={welcomeDone}
        />
      )}
      <Navbar
        user={profile || user}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate('/square')}
      />

      <RouteLoadError error={loadError} onRetry={load} />
      {(town?.v2Enabled || (loading && v2Hint)) ? (
        <TownSquareScreen
          square={square}
          town={town}
          feed={feed}
          loading={loading && !town}
          extrasLoading={extrasLoading}
          presence={presence}
          onChangeTown={openPicker}
          onReload={load}
          onOpenReel={openReel}
        />
      ) : (
      <>
      <SquareHeader
        square={square}
        onChangeTown={openPicker}
        isAdmin={isAdmin}
        onRegenerate={handleRegenerate}
        regenerating={regenerating}
      />

      <div className="mx-auto max-w-3xl px-4 py-6">
        {/* City pills — towns with signals right now, quick-switch */}
        {(() => {
          const pills = nearby.filter((t) => t.liveCount > 0);
          if (pills.length === 0) return null;
          return (
            <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
              {pills.map((t) => {
                const isCurrent = t.slug === citySlug;
                return (
                  <button
                    key={t.slug}
                    type="button"
                    onClick={() => goToTown(t)}
                    className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap border-2 border-black px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wide transition-colors ${
                      isCurrent ? 'text-black' : 'bg-white text-black hover:bg-gray-100'
                    }`}
                    style={isCurrent ? { background: cyan } : undefined}
                  >
                    {t.label}
                    {t.liveCount > 0 && (
                      <span className={`inline-flex min-w-[16px] items-center justify-center px-1 text-[9px] ${isCurrent ? 'bg-black text-white' : 'bg-black text-white'}`}>
                        {t.liveCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Open a Portal — cyan Town-Square CTA */}
        <button
          type="button"
          onClick={() => navigate('/portals/new')}
          className="mb-5 flex w-full items-center justify-center gap-2 border-2 border-black py-3 font-mono text-sm font-bold uppercase tracking-widest text-black transition-opacity hover:opacity-90"
          style={{ background: cyan }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <circle cx="12" cy="7" r="2" /><path d="M12 9V21" /><path d="M8.4 3.6a5.5 5.5 0 0 0 0 6.8" /><path d="M15.6 3.6a5.5 5.5 0 0 1 0 6.8" />
          </svg>
          Open a Portal
        </button>

        {/* Square rules */}
        <div className="mb-5 border-2 border-black bg-white p-3 font-mono text-[11px] text-gray-600">
          <span className="font-bold uppercase tracking-wide text-black">City rules · </span>
          One town, everyone&apos;s here. Be real, be kind. Curated by default — hosts pick who&apos;s in.
          Location reveals only after you&apos;re in.
        </div>

        {loading ? (
          <div className="border-2 border-black bg-white p-10"><LoadingSpinner label="Loading your city…" /></div>
        ) : feed.length === 0 ? (
          <div className="border-2 border-black bg-white p-8 text-center font-mono text-xs uppercase tracking-wider text-gray-500">
            No live Portals here yet. Be the first — open a Portal.
          </div>
        ) : (
          <div className="space-y-3">
            {feed.map((s) => (
              <div key={`sq-${s.eventId}`}>
                <SignalCard signal={s} onChange={load} />
                {!s.isMine && (
                  <div className="mt-1 flex gap-4">
                    <button type="button" onClick={() => setReportFor(s)}
                      className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-red-600">
                      ⚑ Report
                    </button>
                    <button type="button" onClick={() => muteHost(s)}
                      className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-black">
                      🔕 Mute {s.hostDisplayName?.split(' ')[0] || 'them'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      )}

      {openReelUserId && reelList.length > 0 && (
        <StoryViewer key={`${openReelUserId}:${openNonce}`} reels={reelList} startUserId={openReelUserId} doorRect={doorRect} onClose={closeReel} />
      )}

      {reportFor && (
        <Modal isOpen onClose={() => { setReportFor(null); setReportBusy(false); }} title="Report this Portal">
          <p className="mb-3 font-mono text-xs text-gray-500">This is private — they’re never told who reported.</p>
          <div className="grid grid-cols-2 gap-2">
            {[['spam', 'Spam'], ['creep', 'Creepy'], ['not_real', 'Not real'], ['other', 'Something else']].map(([val, label]) => (
              <button key={val} type="button" disabled={reportBusy} onClick={() => submitReport(val)}
                className={modalChoice(false)}>
                {label}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {pickerOpen && (
        <Modal isOpen onClose={() => setPickerOpen(false)} title="CHANGE TOWN">
          <p className="mb-3 font-mono text-xs text-gray-500">
            Your city portal follows you. Search any city worldwide, or jump to a town with Portals.
          </p>

          {/* Global city search — any town, anywhere */}
          <div className="mb-4">
            <CityPicker value={null} onChange={goToCity} nearLocation={readLocation()} />
          </div>

          <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400">
            Towns within 50 miles
          </div>
          {nearby.length === 0 ? (
            <div className="py-6"><LoadingSpinner size="sm" label="Finding towns…" /></div>
          ) : (
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {nearby.map((t) => {
                const isCurrent = t.slug === citySlug;
                return (
                  <button
                    key={t.slug}
                    type="button"
                    onClick={() => goToTown(t)}
                    className={`flex w-full items-center justify-between ${modalRow(isCurrent)}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold uppercase tracking-wide">{t.label}</span>
                      <span className={`font-mono text-[10px] ${isCurrent ? 'text-white/70' : 'text-gray-500'}`}>
                        {isCurrent ? 'Where you are now' : (t.region || 'City portal')}
                        {t.distanceMiles > 0 ? ` · ${t.distance ?? Math.round(t.distanceMiles)} ${t.distanceUnit || 'mi'}` : ''}
                      </span>
                    </span>
                    <span className={`ml-2 flex-shrink-0 font-mono text-[10px] font-bold uppercase ${isCurrent ? 'text-white' : 'text-gray-600'}`}>
                      {t.liveCount > 0 ? `${t.liveCount} live` : '—'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
